import { useFormik } from "formik";
import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useEffect, useRef, useMemo,
} from "react";
import * as Yup from "yup";
import {
  callApiStatus, caseStatus,
  fileNameType, LABEL_WIDTH,
  reportStatusLabel, testResultRequiredRowName,
} from "../../../constants";
import {
  defaultFormatNumberOfPeople,
  defaultFormatNumberOfSample, getAddressFromComponent,
} from "../../../utils";
import CaseStatusCell from "../../../components/Table/Project/TestScheduleTableInfo/CaseStatusCell";
import {
  commonServices,
  reportServices,
} from "../../../services/api";
import { toast } from "react-toastify";
import messages from "../../../services/api/messages";
import moment from "moment";
import InputInForm from "../../../components/UI/Form/Input/InputInForm";
import validateMessages from "../../../validateMessages";
import PrimaryBtn from "../../../components/UI/Button/PrimaryBtn";
import { Box, Modal, useMediaQuery } from "@mui/material";
import InputText from "../../../components/UI/Form/Input/InputText";
import SelectPicStaff from "../../../components/UI/Form/Select/SelectPicStaff";
import ReportPicTable from "../CreateAndUpdatePage/ReportPicTable";
import ReportMaterialInfosTable from "../CreateAndUpdatePage/ReportMaterialInfosTable";
import readXlsxFile from "read-excel-file";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import Button from "app/components/UI/Button";

const Item = forwardRef(
  ({ data, fileDataArray, scheduleId, setAvailableData, setDownloading }, ref) => {
    const isMd = useMediaQuery("(max-width:768px)");

    const inputRef = useRef();

    const [errorsField, setErrorsField] = useState({});
    const [selectDeleteConfirmId, setSelectDeleteConfirmId] = useState(null);
    const [selectConfirmId, setSelectConfirmId] = useState(null);

    const disableChangeStatus = useMemo(() => {
      return data.caseStatus === caseStatus.lost || data.caseStatus === caseStatus.completed;
    }, [data.caseStatus]);

    const [loadInitState, setLoadInitState] = useState({
      loadingStatus: callApiStatus.idle,
      data: null,
      error: null,
    });

    const caseId = data?.caseId || "";
    const customerId = data?.customer?._id || "";
    const customerIdField = data?.customer?.customerId || "";
    const fileType = fileNameType["report"];
    const timeStamp = moment().format("YYYYMMDDHHmmss");

    const loadInitReport = useCallback(() => {
      setLoadInitState({
        loadingStatus: callApiStatus.loading,
        data: null,
        error: null,
      });

      reportServices
        .getInitNewReport(data.customer._id, data.quotation)
        .then((initReport) => {

          if (data.requestFormInfo) {
            initReport.companyNameKanji = data.requestFormInfo.customerName;
            initReport.companyAddress = getAddressFromComponent({
              postalCode: data.requestFormInfo.customerPostalCode,
              prefecture: data.requestFormInfo.customerAddress,
            });
            initReport.reportCustomerPIC.department = data.requestFormInfo.department;
            initReport.reportCustomerPIC.nameKanji = data.requestFormInfo.picNameKanji;
            initReport.reportCustomerPIC.phone = data.requestFormInfo.phone1;
            initReport.reportCustomerPIC.companyNameKanji = initReport.companyNameKanji;
            initReport.reportCustomerPIC.companyAddress = initReport.companyAddress;
            initReport.reportCustomerPIC.reportCreationMethod = data.requestFormInfo.reportCreationMethod ?? '';
          }

          setLoadInitState({
            loadingStatus: callApiStatus.success,
            data: {
              ...initReport,
              reportTestMaterialInfos: fileDataArray,
              generatedPdf: null,
              generatedWord: null,
            },
            error: null,
          });
        })
        .catch((error) => {
          toast.error(messages.callApiFail);
          setLoadInitState({
            loadingStatus: callApiStatus.error,
            data: null,
            error: error,
          });
        });
    }, [data, fileDataArray]);

    const handleCreateReport = async () => {
      setDownloading(true);

      const createFileDownloadData = {
        customerId,
        testScheduleId: scheduleId,
        testId: loadInitState.data.testId,
        doctor: values.doctor,
        reportCreationMethod: values.reportCreationMethod,
        hasTestPlan: !!data?.testPlanInfo,
        reportTestMaterialInfos: values.reportTestMaterialInfos,
        reportCustomerPIC: {
          nameKanji: values?.nameKanji || "",
          phone: values?.phone || "",
          reportCreationMethod: values?.reportCreationMethod || "",
          companyNameKanji: loadInitState.data?.companyNameKanji || "",
          companyAddress: loadInitState.data?.companyAddress || "",
        },
        file: values.file,
      };

      const confirmResult = await reportServices
        .createFileDownload(createFileDownloadData)
        .then((res) => {
          return res.data?.result;
        })
        .catch(() => {
          toast.error(messages.confirm(false));
          setDownloading(false);
        });

      const generatedWordIds = confirmResult?.generatedWords?.map(item => {
        return item._id;
      });

      const createReportData = {
        customerId,
        testScheduleId: scheduleId,
        caseInfoId: data._id,
        testId: loadInitState.data.testId,
        doctor: values.doctor,
        reportCustomerPIC: {
          nameKanji: values?.nameKanji || "",
          phone: values?.phone || "",
          reportCreationMethod: values?.reportCreationMethod || "",
          companyNameKanji: loadInitState.data?.companyNameKanji || "",
          companyAddress: loadInitState.data?.companyAddress || "",
        },
        reportTestMaterialInfos: Array.isArray(
          values.reportTestMaterialInfos
        )
          ? values.reportTestMaterialInfos.map(
              (item) => ({
                name: item.name,
                preprocess: item.preprocess,
                materialNumber: item.materialNumber,
                capacity: item.capacity,
              })
            )
          : [],
        testResult: "",
        generatedPdf: generatedWordIds,
        generatedWord: generatedWordIds,
      };

      Promise.all([
        confirmResult?.generatedWords?.map(async (file) => {
          await commonServices
            .downloadWithFileId(
              file._id,
              "docx",
              file.fileName || `${fileType}_${caseId}_${data?.customer?.companyNameKanji}_${timeStamp}`
            )
            .catch(() => {
              toast.error(messages.downloadFail);
              setDownloading(false);
            });
        }),
        reportServices.createReport(createReportData).then(() => {
          toast.success(messages.create(true));
        }),
      ]).finally(() => {
        setDownloading(false);
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      });
    };

    const createReport = () => {
      if (values.isConfirmed) {
        handleCreateReport();
      }
    };

    const { handleSubmit, values, touched, errors, setFieldValue } =
      useFormik({
        initialValues: {
          doctor: loadInitState.data?.doctor,
          nameKanji: data.requestFormInfo?.picNameKanji,
          phone: data.requestFormInfo?.phone1,
          reportCreationMethod: data.requestFormInfo?.reportCreationMethod,
          file: null,
          isConfirmed: false,
          reportTestMaterialInfos: [],
        },
        validationSchema:
          fileDataArray && fileDataArray.length > 0
            ? Yup.object().shape({
                doctor: Yup.string().required(
                  validateMessages.required("判定医師")
                ),
              })
            : Yup.object().shape({
                doctor: Yup.string(),
                nameKanji: Yup.string(),
                phone: Yup.string(),
              }),
        onSubmit: createReport,
        enableReinitialize: true,
      });

    // send submit function to the parent component
    useImperativeHandle(ref, () => ({
      triggerClick: () => {
        handleSubmit();
      },
    }));

    useEffect(() => {
      if (errors.doctor) {
        toast.error(messages.required("判定医師"));
      }
    }, [errors]);

    useEffect(() => loadInitReport(), [loadInitReport]);

    const handleChangeFile = (e) => {
      try {
        const file = e.target.files[0];
        readXlsxFile(file, { sheet: "list" }).then((rows) => {
          const filterFileData = rows
            .slice(1)
            .filter((row) => {
              const name = row?.[3] || "";
              if (testResultRequiredRowName.includes(name))
                return false;

              const caseId = row?.[6] || "";
              if (caseId !== data.caseId) {
                return false;
              }

              const sheetCustomerId = row?.[4] || "";
              if (customerIdField?.trim() === sheetCustomerId?.trim()) return true;
              return false;
            })
            .map((row) => ({
              name: row?.[3] || "",
              preprocess: row?.[9] || "",
              materialNumber: row?.[1] || "",
              capacity: row?.[10] || "",
            }));

          setFieldValue("file", file);
          setFieldValue("reportTestMaterialInfos", filterFileData);
        });
      } catch (error) {
        toast.error(messages.callApiFail);
      }
    };

    useEffect(() => {
      setAvailableData((prev) => {
        let newData = [...prev];

        if (values.isConfirmed) {
          if (!newData.includes(data.caseId)) {
            newData.push(data.caseId)
          }
        } else {
          if (newData.includes(data.caseId)) {
            newData = newData.filter((caseId) => caseId !== data.caseId)
          }
        }

        return newData
      });

    }, [data.caseId, setAvailableData, values.isConfirmed]);

    const cellStyle = `flex items-center justify-center p-2 overflow-hidden whitespace-nowrap border border-[#B3B3B3] ${
      values?.isConfirmed ? "bg-yellow-200" : ""
    }`;

    return (
      <React.Fragment>
        <div className={"border-l " + cellStyle}>{data.caseId}</div>
        <div className={cellStyle}>
          {reportStatusLabel[data.reportStatus]}
        </div>
        <div className={cellStyle}>
          {data?.customer?.customerId || ""}
        </div>
        <div className={cellStyle}>
          {defaultFormatNumberOfPeople(data.samplePeopleNumber)}
        </div>
        <div className={cellStyle}>
          {defaultFormatNumberOfSample(data.sampleNumber)}
        </div>
        <InputInForm
          type="text"
          value={values.doctor}
          onChange={(value) => {
            setFieldValue("doctor", value);
          }}
          className={cellStyle}
          error={fileDataArray?.length > 0 && errors.doctor}
          touched={touched.doctor}
          otherStyle={
            values?.isConfirmed ? "bg-yellow-200" : ""
          }
        />
        <InputInForm
          type="text"
          value={values.nameKanji}
          onChange={(value) => {
            setFieldValue("nameKanji", value);
          }}
          className={cellStyle}
          error={errors.nameKanji}
          touched={touched.nameKanji}
          otherStyle={
            values?.isConfirmed ? "bg-yellow-200" : ""
          }
        />
        <InputInForm
          type="text"
          value={values.phone}
          onChange={(value) => {
            setFieldValue("phone", value);
          }}
          className={cellStyle}
          error={errors.phone}
          touched={touched.phone}
          otherStyle={
            values?.isConfirmed ? "bg-yellow-200" : ""
          }
        />
        <CaseStatusCell
          cellContent={data.caseStatus}
          textAlign="center"
        />
        <div className={cellStyle}>
          {values.file && values.reportTestMaterialInfos.length > 0 ? (
            <div className="flex items-center gap-2">
              <p className="max-w-[70px] overflow-hidden truncate text-xs">
                {values.file.name}
              </p>
              <button
                type={'button'}
                onClick={() => setSelectDeleteConfirmId('delete-file-' + data.caseId)}
                className="text-[#1976d2] text-[0.8125em] underline"
              >クリア</button>

              <ConfirmDeleteModal
                open={selectDeleteConfirmId === 'delete-file-' + data.caseId}
                text={'選択中のファイルをクリアしたいですか？'}
                closeModal={() => setSelectDeleteConfirmId(null)}
                resetModal={() => {
                  setSelectDeleteConfirmId(null);
                  setFieldValue('isConfirmed', false);
                  setFieldValue('file', null);
                  setFieldValue('reportTestMaterialInfos', []);
                }}
              />

              {values.isConfirmed ? (
                <>
                  <button
                    type={'button'}
                    onClick={() => setSelectDeleteConfirmId('delete-confirmed-' + data.caseId)}
                    className="text-[#1976d2] text-[0.8125em] underline"
                  >確認済
                  </button>

                  <ConfirmDeleteModal
                    open={selectDeleteConfirmId === 'delete-confirmed-' + data.caseId}
                    closeModal={() => setSelectDeleteConfirmId(null)}
                    resetModal={() => {
                      setSelectDeleteConfirmId(null);
                      setFieldValue('isConfirmed', false);
                    }}
                  />
                </>
              ) : (
                <>
                  <PrimaryBtn
                    text={'確認へ'}
                    onClick={() => {
                      setSelectConfirmId(data.caseId)
                    }}
                    className="!h-[30px] !w-16"
                  />

                  <Modal
                    open={selectConfirmId === data.caseId}
                    onClose={() => {
                      setSelectConfirmId(null);
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: isMd ? "90%" : "75%",
                        maxHeight: "85%",
                        bgcolor: "white",
                        boxShadow: 24,
                        p: isMd ? 2 : 4,
                        borderRadius: 2,
                        overflowY: 'auto',
                      }}
                      className="bg-white"
                    >
                      <div className="flex items-center gap-4 mb-6">
                        <p>試験番号</p>
                        <p>{data.quotationInfo?.quotationId || ''}</p>
                      </div>

                      <div className="w-full h-auto flex items-center justify-center mb-6 flex-wrap">
                        <div className="w-full mb-3 md:w-1/2 h-full">
                          <SelectPicStaff
                            pic={{id: loadInitState?.data?.picStaff}}
                            labelWidth={isMd ? LABEL_WIDTH.LEFT : 70}
                            disabled={true}
                          />
                        </div>

                        <div className="w-full mb-3 md:w-1/2 h-full">
                          <InputText
                            value={values.doctor}
                            onChange={(newValue) => {
                              setFieldValue("doctor", newValue)
                            }}
                            label="判定医師"
                            labelWidth={LABEL_WIDTH.LEFT}
                            errors={errorsField.doctor}
                            touched={true}
                            isShowError={true}
                            isRequired
                          />
                        </div>
                      </div>

                      <div className="mb-[14px]">
                        <div className="w-full h-auto mb-5 overflow-x-auto">
                          <p className="text-custom-size mb-3">報告書の内容</p>
                          <ReportPicTable
                            companyNameKanji={loadInitState?.data?.companyNameKanji}
                            companyAddress={loadInitState?.data?.companyAddress}
                            reportPICInfo={{
                              nameKanji: values?.nameKanji,
                              phone: values?.phone,
                              reportCreationMethod: values?.reportCreationMethod,
                            }}
                            setReportPICInfo={(newValue) => {
                              setFieldValue("nameKanji", newValue.nameKanji)
                              setFieldValue("phone", newValue.phone)
                              setFieldValue("reportCreationMethod", newValue.reportCreationMethod)
                            }}
                          />
                        </div>

                        <div className="w-full h-auto mb-5 overflow-x-auto">
                          <p className="text-custom-size mb-3">被験物質の概要</p>
                          <ReportMaterialInfosTable
                            reportTestMaterialInfos={
                              values?.reportTestMaterialInfos
                            }
                            setReportTestMaterialInfos={(newValue) => {
                              setFieldValue('reportTestMaterialInfos', newValue);
                            }}
                          />
                        </div>
                      </div>

                      <div className="w-full h-auto flex items-center justify-end gap-2 mb-3 flex-wrap">
                        <Button
                          disabled={!values?.reportTestMaterialInfos || values?.reportTestMaterialInfos.length === 0}
                          type={values?.reportTestMaterialInfos?.length > 0 ? "primary" : "disabled"}
                          onClick={() => {
                            if (values?.doctor) {
                              setSelectConfirmId(null)
                              setFieldValue("isConfirmed", true)
                              setErrorsField({})
                            } else {
                              setErrorsField({ doctor: true })
                            }
                          }}
                        >
                          確認する
                        </Button>
                        <PrimaryBtn
                          text={'キャンセル'}
                          onClick={() => {
                            setSelectConfirmId(null)
                            setErrorsField({})
                          }}
                        />
                      </div>
                    </Box>
                  </Modal>
                </>
              )}
            </div>
          ) : (
            <div className="relative">
              <div className="flex items-center gap-2">
                <p className="text-sm">試験結果</p>
                <div
                  className={`p-2 text-sm border border-[#B3B3B3] !outline-none bg-[#ebebeb] cursor-pointer ${disableChangeStatus ? 'opacity-[0.5]' : ''}`}
                  onClick={() => {
                    !disableChangeStatus && inputRef.current && inputRef.current.click();
                  }}
                >
                  <p className="max-w-[90px] text-xs">
                    ファイルを選択
                  </p>
                </div>
              </div>
              <input
                ref={inputRef}
                id={''}
                name={''}
                type="file"
                onChange={handleChangeFile}
                className="hidden"
              />
            </div>
          )}
        </div>
        <div className={cellStyle}>
          {values?.file
            ? values?.reportTestMaterialInfos?.length > 0
              ? "有"
              : "無"
            : null}
        </div>
      </React.Fragment>
    );
  }
);

export default Item;
